import type { CityConfig } from "../city-template/types";

export const sparksConfig: CityConfig = {
  city: "Sparks",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "sparks",
  route: "/cities/sparks",
  title: "Emergency Passport Sparks NV | Expedited Passport Renewal Washoe County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Sparks, NV — as fast as 24 hours. Fast passport services for Sparks, Washoe County, and Eastern Reno-Sparks metro travelers. A+ BBB rated, registered State Dept. courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Sparks, NV",
  heroSubtitle: "Passport expired before your trip from the Reno-Sparks area? Fast Passport Center helps Sparks and Washoe County travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada has no local passport agency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sparks%20Nevada%20city%20skyline%20from%20Vista%20Boulevard%20with%20Truckee%20Meadows%20valley%20view%2C%20Sierra%20Nevada%20mountains%20with%20snow%20caps%20in%20background%2C%20modern%20commercial%20district%20and%20residential%20neighborhoods%20visible%20from%20elevated%20perspective%2C%20warm%20afternoon%20desert%20light%2C%20wide%20panoramic%20landscape%20photography%2C%20clean%20Nevada%20sky%20with%20scattered%20clouds%2C%20Sparks%20Marina%20blue%20water%20visible%20in%20mid-ground%2C%20industrial%20and%20tech%20corridor%20with%20Tesla%20Gigafactory%20context%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=sparks-nv-hero-001&orientation=landscape",
  heroImageAlt: "Sparks Nevada city view from Vista Boulevard with Truckee Meadows and Sierra Nevada mountains",
  geoPosition: "39.5349;-119.7527",
  geoPlacename: "Sparks, Nevada",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St, Suite 600, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 220 miles from Sparks via I-80",
  },
  nearbyCitiesZips: [
    { name: "Sparks", zips: "89431, 89434, 89436" },
    { name: "East Sparks", zips: "89436, 89441" },
    { name: "Spanish Springs", zips: "89441" },
    { name: "Sun Valley", zips: "89433" },
    { name: "Reno", zips: "89501, 89502, 89503" },
    { name: "Fernley", zips: "89408" },
    { name: "Dayton", zips: "89403" },
    { name: "Silver Springs", zips: "89429" },
    { name: "Fallon", zips: "89406" },
    { name: "Carson City", zips: "89701" },
    { name: "Incline Village", zips: "89451" },
    { name: "Verdi", zips: "89439" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Sparks and Reno-Sparks departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Sparks or Reno" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Reno", route: "/cities/reno" },
    { name: "Fast passport renewal in Las Vegas", route: "/cities/las-vegas" },
    { name: "Expedited passport help in Henderson NV", route: "/cities/henderson-nv" },
    { name: "Urgent passport services in Sacramento", route: "/cities/sacramento" },
    { name: "Same-day passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Passport courier services in Northern Nevada", route: "/states/nevada" },
  ],
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  fpcOfficeNote: "Fast Passport Center serves Sparks remotely via secure courier. We do not have a walk-in office in Sparks — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Sparks and Eastern Washoe County travelers.",
  fpcOfficeDistance: "Serving all of Sparks, Eastern Washoe County, and the Reno-Sparks Metro",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Sparks, Spanish Springs, Sun Valley, and the eastern Reno-Sparks metro. As the technology corridor grows with companies like Tesla's Gigafactory, Panasonic, Amazon, and other logistics giants near Sparks — international business travel demand is higher than ever.",
  nearbyCitiesIntro: "We serve all Sparks neighborhoods and nearby communities including East Sparks, Spanish Springs, Sun Valley, Fernley, Dayton, Silver Springs, Fallon, Incline Village, Verdi, and the entire eastern Washoe County corridor. Our overnight shipping labels work from any UPS location, so your address doesn't slow us down.",
  howItWorksIntro: "Most Sparks travelers complete everything online and ship documents overnight. No need to drive to San Francisco or Sacramento. Our emergency passport process delivers fast results for Eastern Nevada travelers.",
  mistakesIntro: "Sparks and Eastern Washoe County residents often don't plan for the distance to the nearest passport agency. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Sparks and Eastern Reno-Sparks metro travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sparks? Here are the most common ones we hear from Washoe County and Eastern Nevada travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (220 miles)",
        "Donner Pass can add significant travel time",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Sparks travelers with immediate departure needs — as fast as 24 hours for urgent flights from Reno-Tahoe International Airport.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance for Sparks and Eastern Washoe County residents.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Sparks Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Sparks NV and Eastern Washoe County." },
    { name: "New Passport", description: "First-time passport application assistance for Sparks and Eastern Reno-Sparks metro residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jeff H.",
      location: "Spanish Springs, NV",
      rating: 5,
      text: "International engineering conference in Germany — found out my passport expired 5 days before the flight. Fast Passport Center delivered a new one in 40 hours. Saved the whole trip.",
      service: "Emergency Passport",
    },
    {
      name: "Angela S.",
      location: "Sparks, NV",
      rating: 5,
      text: "Tesla employee traveling to Japan for work training. Passport expired 2 months before I checked. They renewed it in 3 days with zero hassle. Five stars every time.",
      service: "Passport Renewal",
    },
    {
      name: "Chris K.",
      location: "Sun Valley, NV",
      rating: 5,
      text: "Family vacation to the Caribbean and my daughter needed her first passport. They guided us through every step and we had her passport in 5 days. Excellent service.",
      service: "New Passport",
    },
    {
      name: "Diana R.",
      location: "Fernley, NV",
      rating: 5,
      text: "Even living in Fernley — 30 miles out — they came through. Overnight shipping, fast processing, passport at my door in 48 hours. Worth every dollar.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "sparks-1",
      name: "Sparks Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1075 Baring Blvd",
      city: "Sparks",
      state: "NV",
      zip: "89434",
      phone: "(775) 355-4405",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Sparks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Sparks facility. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sparks-2",
      name: "Spanish Springs Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6390 N Pyramid Hwy",
      city: "Sparks",
      state: "NV",
      zip: "89436",
      phone: "(775) 626-0291",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Northern Sparks/Spanish Springs area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Spanish Springs and North Valleys. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sparks-3",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles west of Sparks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Downtown Reno. The largest passport facility in the metro area. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "sparks-4",
      name: "Washoe County Government Complex",
      type: "County Clerk Passport Acceptance",
      address: "1001 E 9th St",
      city: "Reno",
      state: "NV",
      zip: "89512",
      phone: "(775) 328-3275",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "5 miles west of Sparks",
      website: "https://www.washoecounty.gov/",
      websiteLabel: "Washoe County Clerk",
      notes: "Full-service county facility with extended hours. Passport acceptance appointments required.",
      icon: "ri-government-line",
    },
    {
      id: "sparks-5",
      name: "Fernley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "440 E Main St",
      city: "Fernley",
      state: "NV",
      zip: "89408",
      phone: "(775) 575-2421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles east of Sparks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Fernley, Silver Springs, and Lyon County travelers. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sparks-6",
      name: "Nevada DMV — Sparks",
      type: "State DMV Acceptance Facility",
      address: "1775 Hymer Ave",
      city: "Sparks",
      state: "NV",
      zip: "89431",
      phone: "(775) 684-4368",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Western Sparks near the Reno border",
      website: "https://dmv.nv.gov/",
      websiteLabel: "Nevada DMV",
      notes: "Full-service Sparks DMV. REAL ID and driver's license renewals. Appointment recommended.",
      icon: "ri-car-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 220 miles to San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Sparks photo shops often use generic settings. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Sparks and Reno area acceptance facilities.",
    },
    {
      icon: "ri-cloud-windy-line",
      title: "Underestimating Winter Mountain Travel",
      description: "Driving to the San Francisco Passport Agency requires crossing Donner Pass — which can be closed or extremely slow in winter. Our courier service eliminates this risk entirely.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Sparks and Spanish Springs families at the acceptance clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency. Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Sparks or Reno." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sparks address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Sparks, NV?",
      a: "Fast Passport Center offers emergency passport processing in Sparks in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest is San Francisco, 220 miles via I-80 over Donner Pass — so our courier service is the fastest, safest option for Sparks travelers.",
    },
    {
      q: "What is the nearest passport agency to Sparks?",
      a: "The nearest passport agency is the San Francisco Passport Agency, approximately 220 miles west via I-80 — over the Sierra Nevada and Donner Pass. In winter, this drive can easily take 5+ hours with mountain conditions. Fast Passport Center's 24–48 hour courier service means you never have to make that drive.",
    },
    {
      q: "Do you serve Tesla Gigafactory and logistics corridor employees in Sparks?",
      a: "Absolutely. The Tahoe-Reno Industrial Center near Sparks — home to Tesla's Gigafactory, Panasonic, Switch, and major logistics companies — generates significant international business travel. Fast Passport Center serves all employees and families in the Sparks industrial corridor with fast passport processing.",
    },
    {
      q: "Where can I get a fast passport renewal in Sparks?",
      a: "Fast Passport Center offers fast passport renewal in Sparks with no office visit required for most adult renewals. We serve all Sparks neighborhoods including Spanish Springs, Sun Valley, East Sparks, and outlying areas like Fernley and Dayton. Ship your documents overnight and we handle the rest.",
    },
    {
      q: "What passport facilities are available in Sparks?",
      a: "Sparks has passport acceptance facilities at the Sparks Main Post Office (89434), Spanish Springs Post Office (89436), and the nearby Nevada DMV on Hymer Ave. For broader options, the downtown Reno Post Office and Washoe County Government Complex are both within 5 miles of Sparks.",
    },
    {
      q: "What if I need a passport for international work travel?",
      a: "Sparks has become a major technology and logistics corridor. If you work at Tesla Gigafactory, Panasonic, Amazon, Switch, or another employer requiring international travel, Fast Passport Center provides fast expedited passport renewal and new passport services tailored to business travelers in the Sparks and Reno-Sparks metro.",
    },
    {
      q: "How much does expedited passport service cost in Sparks?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by speed — emergency 24–48 hour, 3–5 day expedited, or standard. Contact us for a personalized quote based on your departure date.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Sparks", address: "1625 E Prater Way, Sparks, NV 89434", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Sparks", address: "1075 Baring Blvd, Sparks, NV 89434", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Sparks", address: "2355 Market St, Sparks, NV 89434", hours: "Mon–Sun: 6AM–11PM" },
    { name: "UPS Store — Sparks", address: "1845 E Prater Way, Sparks, NV 89434", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Reno (near Sparks)", address: "1201 Terminal Way, Reno, NV 89502", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Walgreens — Spanish Springs", address: "6390 N Pyramid Hwy, Sparks, NV 89436", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — East Sparks", address: "6400 Ingleston Dr, Sparks, NV 89436", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Sparks", address: "989 Sparks Blvd, Sparks, NV 89434", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Sparks",
      address: "1775 Hymer Ave, Sparks, NV 89431",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service Sparks DMV. REAL ID and driver's license. Online appointment required.",
      featured: true,
    },
    {
      name: "Nevada DMV — Reno",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative Northern Nevada DMV for Sparks residents. Full services available.",
      featured: false,
    },
    {
      name: "Washoe County Clerk",
      address: "1001 E 9th St, Reno, NV 89512",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government services with passport acceptance. 5 miles from Sparks.",
      featured: true,
    },
    {
      name: "Nevada DMV — Carson City",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "State capital DMV. Full service for Southern Washoe County and Carson Valley travelers.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "8 miles from Sparks",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "The primary airport for Sparks and Washoe County. Direct service to Mexico and connecting international flights. 8 miles from Sparks via I-80.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "Reno-Tahoe Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "220 miles from Sparks via I-80",
      address: "San Francisco, CA 94128",
      note: "Primary West Coast hub. Home to the San Francisco Passport Agency. Requires crossing the Sierra Nevada over Donner Pass.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "130 miles from Sparks via I-80",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative regional hub accessible via I-80. Some Sparks travelers connect internationally through Sacramento.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};